// src/pages/Legal.jsx
// Privacy Policy, Terms of Service, and Risk Disclaimer for ODFTrades.
//
// Drafted to reflect ODFTrades Inc. as a Canadian-registered company
// operating as an educational trading platform and software business
// — NOT as a financial advisor, broker, dealer, portfolio manager, or
// any kind of registered investment firm.
//
// Plain-English where possible, professional where it matters, with
// explicit trading-risk language and a clear "not financial advice"
// posture appropriate for an educational + software platform.
//
// IMPORTANT: This is draft website disclaimer content, not lawyer-
// approved legal advice. It should be reviewed by a qualified lawyer
// before public launch, and updated to reflect the actual province
// of incorporation, registered business address, and final company
// name.
import React, { useState } from 'react';
import ODFLogo from '../components/ODFLogo';
import HeroCanvas from '../components/HeroCanvas';
import HeroAtmosphere from '../components/HeroAtmosphere';
import { ArrowLeft } from 'lucide-react';

const YEAR = new Date().getFullYear();
const G    = '#00e5a0';

const SECTIONS = {
  privacy: {
    title: 'Privacy Policy',
    updated: `Last updated: ${new Date().toLocaleString('en-CA', { month: 'long', year: 'numeric' })}`,
    intro:
      'This Privacy Policy explains how ODFTrades Inc. ("ODFTrades", "we", "us", or "our") collects, uses, ' +
      'stores, and protects personal information across the ODFTrades educational trading platform and its ' +
      'software products, including Performance Lab and Trading Resources. By using the platform you agree ' +
      'to this Policy.',
    content: [
      {
        heading: '1. Who We Are',
        text:
          'ODFTrades is an educational trading platform and software company operated by ODFTrades Inc., a ' +
          'corporation organised in Canada. ODFTrades was founded by ODF and provides trading education, ' +
          'market-learning content, trader-development resources, journaling, analytics, and practical ' +
          'trading software tools. References to "ODFTrades", "we", "us", or "our" mean ODFTrades Inc. and ' +
          'any product or content area inside the platform (such as Performance Lab and Trading Resources). ' +
          'ODFTrades Inc. operates as an educational and software business — not as a financial advisor, ' +
          'investment advisor, broker, dealer, portfolio manager, or registered investment firm.',
      },
      {
        heading: '2. Information We Collect',
        text:
          'We collect only the information you choose to provide. This includes: (a) account information ' +
          'when you create a Performance Lab account (email address, optional profile fields, optional ' +
          'username); (b) any trading data you choose to log in Performance Lab (trade entries, notes, ' +
          'account settings); (c) email and any optional fields you submit when joining the waitlist, ' +
          'requesting a resource, or contacting us; (d) basic technical information your browser sends ' +
          'automatically (such as device type and approximate region) for security and reliability.',
      },
      {
        heading: '3. How We Use Your Information',
        text:
          'We use your information solely to: (a) operate ODFTrades and its products and deliver the ' +
          'service you signed up for; (b) display your own Performance Lab dashboard analytics and journal ' +
          'data back to you; (c) respond to messages and provide support; (d) send service emails (such as ' +
          'account confirmations, password resets, and resource downloads). We do not sell, rent, or ' +
          'share your personal data with third parties for marketing.',
      },
      {
        heading: '4. Legal Basis (Canada and Beyond)',
        text:
          'In Canada, we process personal information under the Personal Information Protection and ' +
          'Electronic Documents Act (PIPEDA) and applicable provincial privacy law. Where you are located ' +
          'in another jurisdiction (such as the EU/UK or California), we rely on your consent, our ' +
          'legitimate interest in operating the service, and our need to provide what you have requested. ' +
          'You may withdraw consent at any time by contacting us.',
      },
      {
        heading: '5. Data Storage and Security',
        text:
          'Your data is stored on industry-standard cloud infrastructure protected by per-user access ' +
          'policies, encrypted transit (HTTPS/TLS), and access controls. We apply reasonable technical and ' +
          'organisational safeguards to protect against unauthorised access, loss, or disclosure. No ' +
          'system is perfectly secure — by using ODFTrades you acknowledge this risk.',
      },
      {
        heading: '6. Cookies and Local Storage',
        text:
          'ODFTrades uses cookies and local browser storage to remember your preferences, save your ' +
          'session, and store platform settings. These are stored locally on your device and are not used ' +
          'for advertising. You can clear them at any time through your browser.',
      },
      {
        heading: '7. Public Sharing (Performance Lab)',
        text:
          'Performance Lab accounts are private by default. If you choose to make your profile public, ' +
          'only the performance metrics you opt to share become visible via your shareable link. Your ' +
          'account balance is never shown publicly. You can change visibility settings or disable sharing ' +
          'at any time inside Performance Lab.',
      },
      {
        heading: '8. Third-Party Services',
        text:
          'ODFTrades may display third-party widgets for market data (such as charting and economic-' +
          'calendar providers). Those widgets operate under their own privacy policies and we do not ' +
          'control or access the data they collect. If you submit a contact form or join the waitlist, ' +
          'your submission is processed by a form-handling provider on our behalf.',
      },
      {
        heading: '9. Your Rights',
        text:
          'You may request access to, correction of, or deletion of your personal information at any time ' +
          'by emailing info@odftrades.com. You can export your trade data from your Performance Lab ' +
          'Settings page. Where required by law, we will respond to verifiable requests within applicable ' +
          'time limits.',
      },
      {
        heading: '10. Children',
        text:
          'ODFTrades is not directed at children under 18. We do not knowingly collect personal ' +
          'information from children. If you believe a child has provided personal information to us, ' +
          'contact info@odftrades.com so we can delete it.',
      },
      {
        heading: '11. International Users',
        text:
          'ODFTrades is operated from Canada. If you access the platform from outside Canada, your ' +
          'information may be transferred to and stored in Canada and other countries where our service ' +
          'providers operate. By using ODFTrades you consent to this transfer.',
      },
      {
        heading: '12. Changes to this Policy',
        text:
          'We may update this Privacy Policy from time to time. The "Last updated" date at the top of ' +
          'this page reflects the most recent revision. Continued use of ODFTrades after changes ' +
          'constitutes acceptance of the updated Policy.',
      },
      {
        heading: '13. Contact',
        text:
          'For privacy questions or requests, email info@odftrades.com or contact us via the form on the ' +
          'Contact page.',
      },
    ],
  },

  terms: {
    title: 'Terms of Service',
    updated: `Last updated: ${new Date().toLocaleString('en-CA', { month: 'long', year: 'numeric' })}`,
    intro:
      'These Terms govern your use of ODFTrades — the educational trading platform and software company ' +
      'operated by ODFTrades Inc., a corporation organised in Canada — including Performance Lab, Trading ' +
      'Resources, market-learning content, market notes, and any other product or content inside the ' +
      'platform. By accessing or using ODFTrades you agree to be bound by these Terms.',
    content: [
      {
        heading: '1. Acceptance of Terms',
        text:
          'By accessing or using ODFTrades, any product inside it (including Performance Lab and Trading ' +
          'Resources), or any content shared through the platform, you agree to these Terms of Service. ' +
          'If you do not agree, do not use the platform.',
      },
      {
        heading: '2. What ODFTrades Is',
        text:
          'ODFTrades is an educational trading platform and software company operated by ODFTrades Inc. ' +
          'and founded by ODF. The platform provides trading education, market-learning content, ' +
          'trader-development resources, journaling, analytics, and practical software tools. Performance ' +
          'Lab is the main trading performance product inside ODFTrades; Trading Resources is the ' +
          'educational content area; market notes and any founder content, where shared, are also part of ' +
          'the platform. ODFTrades Inc. is a registered corporation operating as an educational and ' +
          'software business — it is not a financial advisor, investment advisor, broker, dealer, ' +
          'portfolio manager, or registered investment firm.',
      },
      {
        heading: '3. Not Financial Advice',
        text:
          'ODFTrades Inc. is not a registered investment dealer, portfolio manager, financial advisor, ' +
          'investment advisor, broker, securities advisor, or registered investment firm in Canada or any ' +
          'other jurisdiction. Nothing on ODFTrades — including Performance Lab analytics, Trading ' +
          'Resources, market-learning content, market notes, trade ideas, dashboard insights, written or ' +
          'spoken content, software outputs, or anything else — is financial advice, investment advice, ' +
          'trading advice, tax advice, legal advice, or a recommendation to buy, sell, hold, or trade any ' +
          'security, financial instrument, or other asset. All content is provided for educational and ' +
          'informational purposes only. Any examples, tools, educational content, analytics, or platform ' +
          'outputs are for educational and informational purposes only. ODFTrades does not guarantee ' +
          'profits, funded account success, trading success, or any specific financial outcome. You should ' +
          'consult a qualified financial, legal, or tax professional in your jurisdiction before making ' +
          'any financial decision.',
      },
      {
        heading: '4. Eligibility and Accounts',
        text:
          'You must be at least 18 years old (or the age of majority in your jurisdiction) to use ODFTrades. ' +
          'You are responsible for maintaining the confidentiality of your account credentials and for all ' +
          'activity under your account. Notify us promptly at info@odftrades.com if you believe your ' +
          'account has been accessed without authorisation.',
      },
      {
        heading: '5. Acceptable Use',
        text:
          'You agree not to: (a) use ODFTrades for any unlawful purpose; (b) attempt unauthorised access to ' +
          'any part of the platform or its infrastructure; (c) misrepresent your trading results, identity, ' +
          'or affiliation; (d) reverse-engineer, scrape, or attempt to extract code, data, or models from ' +
          'the platform; (e) use the platform to deceive other users, distribute malware, or harm any ' +
          'person or system.',
      },
      {
        heading: '6. Intellectual Property',
        text:
          'All content, design, code, branding, and trademarks on ODFTrades — including the Performance ' +
          'Lab software, Trading Resources material, the ODF Score framework, the ODFTrades name and ' +
          'logo, and any market notes or founder content — are owned by ODFTrades Inc. or its licensors. ' +
          'You may not copy, reproduce, redistribute, or create derivative works from any part of the ' +
          'platform without our prior written permission.',
      },
      {
        heading: '7. Your Content',
        text:
          'You retain ownership of trade data, notes, and any content you upload to your Performance Lab ' +
          'account. You grant ODFTrades a limited, worldwide, non-exclusive licence to host, store, ' +
          'process, and display your content solely for the purpose of operating the service you have ' +
          'signed up for.',
      },
      {
        heading: '8. Third-Party Content and Links',
        text:
          'ODFTrades may display or link to third-party content (such as charts, economic-calendar data, ' +
          'or external websites). We do not own or control that content and are not responsible for it. ' +
          'Your use of any third-party service is governed by that service’s own terms and policies.',
      },
      {
        heading: '9. Disclaimers',
        text:
          'ODFTrades is provided "as is" and "as available" without warranties of any kind, express or ' +
          'implied, including without limitation warranties of merchantability, fitness for a particular ' +
          'purpose, non-infringement, accuracy, or uninterrupted operation. We do not warrant that the ' +
          'platform will be error-free, secure, or always available.',
      },
      {
        heading: '10. Limitation of Liability',
        text:
          'To the maximum extent permitted by Canadian law, ODFTrades Inc., its directors, officers, ' +
          'employees, and agents are not liable for any indirect, incidental, special, consequential, or ' +
          'punitive damages, or for any loss of profits, revenues, data, or trading capital, arising out ' +
          'of or in connection with your use of ODFTrades — even if we have been advised of the possibility ' +
          'of such damages. Trading involves substantial risk of loss. You alone are responsible for your ' +
          'trading decisions.',
      },
      {
        heading: '11. Indemnification',
        text:
          'You agree to indemnify and hold ODFTrades Inc. harmless from any claim, loss, or expense ' +
          '(including reasonable legal fees) arising from your use of the platform, your violation of ' +
          'these Terms, or your violation of any third-party right.',
      },
      {
        heading: '12. Suspension and Termination',
        text:
          'We may suspend or terminate your access to ODFTrades or any product inside it, with or without ' +
          'notice, for conduct we believe violates these Terms, harms other users, or harms the platform.',
      },
      {
        heading: '13. Governing Law',
        text:
          'These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada ' +
          'applicable therein, without regard to conflict-of-law principles. You agree to submit to the ' +
          'exclusive jurisdiction of the courts located in Ontario, Canada for any dispute that cannot be ' +
          'resolved informally.',
      },
      {
        heading: '14. Changes to these Terms',
        text:
          'We may update these Terms from time to time. Material changes will be reflected in the ' +
          '"Last updated" date. Continued use of ODFTrades after changes constitutes acceptance of the ' +
          'updated Terms.',
      },
      {
        heading: '15. Contact',
        text:
          'Questions about these Terms? Email info@odftrades.com.',
      },
    ],
  },

  disclaimer: {
    title: 'Risk Disclaimer',
    updated: `Last updated: ${new Date().toLocaleString('en-CA', { month: 'long', year: 'numeric' })}`,
    intro:
      'Trading the financial markets involves real risk. Please read this Disclaimer carefully. It applies ' +
      'to everything on ODFTrades — the educational trading platform and software company operated by ' +
      'ODFTrades Inc. — including Performance Lab, Trading Resources, market-learning content, market ' +
      'notes, founder content, dashboard insights, software outputs, and any other material on the platform.',
    content: [
      {
        heading: 'What ODFTrades Is (And Is Not)',
        text:
          'ODFTrades is an educational trading platform and software company. The platform provides ' +
          'trading education, market-learning content, trader-development resources, journaling, ' +
          'analytics, and practical software tools. ODFTrades Inc. is a registered corporation operating ' +
          'as an educational and software business. ODFTrades Inc. is NOT a financial advisor, investment ' +
          'advisor, broker, dealer, portfolio manager, securities advisor, or registered investment firm ' +
          'in Canada or any other jurisdiction.',
      },
      {
        heading: 'Trading Carries Substantial Risk',
        text:
          'Trading financial instruments — including foreign exchange (forex), futures, cryptocurrencies, ' +
          'stocks, indices, options, contracts for difference (CFDs), and commodities — involves substantial ' +
          'risk of loss and is not suitable for everyone. The high degree of leverage available in some ' +
          'markets can work against you as well as for you. You can lose some or all of your capital. ' +
          'Do not trade with money you cannot afford to lose.',
      },
      {
        heading: 'Not Financial Advice',
        text:
          'Nothing on ODFTrades — including Performance Lab analytics, Trading Resources, market-learning ' +
          'content, market notes, trade ideas, founder updates, dashboard insights, software outputs, or ' +
          'any other material — is financial advice, investment advice, trading advice, tax advice, legal ' +
          'advice, or a recommendation to buy, sell, hold, or trade any security, financial instrument, or ' +
          'other asset. Any examples, tools, educational content, analytics, or platform outputs are ' +
          'provided for educational and informational purposes only.',
      },
      {
        heading: 'No Guaranteed Profits or Outcomes',
        text:
          'ODFTrades does not guarantee profits, returns, funded account success, trading success, or any ' +
          'specific financial outcome. Past performance — whether shown in Performance Lab dashboard ' +
          'analytics, founder market notes, public or private profiles, or anywhere else — is not ' +
          'indicative of and does not guarantee future results.',
      },
      {
        heading: 'Performance Lab Is For Self-Review',
        text:
          'Performance Lab analytics, dashboards, journals, scoring, and review tools exist to help you ' +
          'study your own past trading activity. They are tools for self-review and self-improvement only. ' +
          'They are not financial advice and are not predictive of any future market behaviour.',
      },
      {
        heading: 'Self-Reported Data',
        text:
          'Performance metrics shown on shareable dashboard analytics and public profiles inside ' +
          'Performance Lab are self-reported by the user who logged them. ODFTrades does not independently ' +
          'verify trading results. Users are solely responsible for the accuracy of the data they enter.',
      },
      {
        heading: 'Market Notes, Founder Content, and Dashboard Insights',
        text:
          'If ODFTrades shares market notes, trade ideas, founder commentary, or live dashboard insights ' +
          'from ODF or anyone associated with the platform, those materials are shared for education, ' +
          'transparency, and trader development only. They are not signals. They are not recommendations. ' +
          'They are not guarantees. Acting on any of that material is entirely at your own discretion and risk.',
      },
      {
        heading: 'You Are Responsible',
        text:
          'All trading decisions are made independently by you. You alone are responsible for evaluating ' +
          'your own financial situation, risk tolerance, time horizon, regulatory environment, and trading ' +
          'choices. You are responsible for your own trading decisions, risk management, and financial ' +
          'outcomes. ODFTrades Inc. and its directors, officers, employees, and agents bear no ' +
          'responsibility for trading losses or other consequences incurred by users of the platform.',
      },
      {
        heading: 'Regional Restrictions',
        text:
          'Trading certain instruments — including CFDs and leveraged crypto products — is restricted or ' +
          'prohibited in some jurisdictions. It is your responsibility to ensure that any trading you ' +
          'engage in is lawful where you reside.',
      },
      {
        heading: 'Seek Professional Advice',
        text:
          'If you are uncertain about any aspect of trading, taxes, or investing, consult a licensed ' +
          'financial advisor, tax professional, or lawyer in your jurisdiction before making any decision.',
      },
      {
        heading: 'Contact',
        text:
          'Questions about this Disclaimer? Email info@odftrades.com.',
      },
    ],
  },
};

export default function Legal({ page = 'privacy', onBack, onSignIn }) {
  const [active, setActive] = useState(page);
  const section = SECTIONS[active];

  return (
    <div
      style={{
        background: '#0a0a0f', minHeight: '100vh',
        color: '#f0f0f5', fontFamily: "'IBM Plex Sans', sans-serif",
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Ambient atmosphere — same as the rest of the site */}
      <HeroCanvas />
      <HeroAtmosphere />

      <nav
        style={{
          position: 'sticky', top: 0, zIndex: 50,
          background: 'rgba(10,10,15,0.95)', backdropFilter: 'blur(12px)',
          borderBottom: '1px solid #1e1e2e', padding: '0 28px', height: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}
      >
        <ODFLogo size="compact" />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={onBack}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'transparent', border: '1px solid #2a2a3e',
              borderRadius: 100, padding: '7px 14px',
              fontSize: 12, color: '#8888a8', cursor: 'pointer',
              fontFamily: "'IBM Plex Sans', sans-serif",
              transition: 'border-color 0.2s, color 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = G; e.currentTarget.style.color = '#f0f0f5'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a3e'; e.currentTarget.style.color = '#8888a8'; }}
          >
            <ArrowLeft size={13} /> Back
          </button>
          <button
            onClick={onSignIn}
            style={{
              background: G, color: '#0a0a0f', border: 'none', borderRadius: 8,
              padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}
          >
            Performance Lab →
          </button>
        </div>
      </nav>

      <div style={{ position: 'relative', zIndex: 5, maxWidth: 820, margin: '0 auto', padding: '60px 24px' }}>
        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 36, flexWrap: 'wrap' }}>
          {Object.entries(SECTIONS).map(([key, s]) => (
            <button
              key={key}
              onClick={() => setActive(key)}
              style={{
                padding: '8px 20px', borderRadius: 8, border: '1px solid',
                fontSize: 13, cursor: 'pointer', outline: 'none',
                fontFamily: "'IBM Plex Sans', sans-serif",
                background: active === key ? 'rgba(0,229,160,0.1)' : 'transparent',
                borderColor: active === key ? 'rgba(0,229,160,0.3)' : '#1e1e2e',
                color: active === key ? G : '#8888a8',
                transition: 'all 0.2s',
              }}
            >
              {s.title}
            </button>
          ))}
        </div>

        <div style={{ marginBottom: 8, fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace" }}>
          {section.updated}
        </div>
        <h1
          style={{
            fontSize: 'clamp(26px, 4vw, 42px)',
            fontWeight: 600, letterSpacing: '-0.025em',
            margin: '0 0 24px',
          }}
        >
          {section.title}
        </h1>

        {section.intro && (
          <p
            style={{
              fontSize: 14, color: '#8888a8', lineHeight: 1.9,
              margin: '0 0 36px', padding: '16px 18px',
              background: 'rgba(0,229,160,0.04)',
              border: '1px solid rgba(0,229,160,0.15)',
              borderRadius: 10,
            }}
          >
            {section.intro}
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {section.content.map((item, i) => (
            <div key={i}>
              <h2
                style={{
                  fontSize: 15.5, fontWeight: 600, color: '#f0f0f5',
                  margin: '0 0 10px', letterSpacing: '-0.005em',
                }}
              >
                {item.heading}
              </h2>
              <p style={{ fontSize: 14, color: '#8888a8', lineHeight: 1.85, margin: 0 }}>
                {item.text}
              </p>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: 48, paddingTop: 24,
            borderTop: '1px solid #1e1e2e',
            fontSize: 12, color: '#55556a',
            fontFamily: "'DM Mono', monospace",
            lineHeight: 1.7,
          }}
        >
          © {YEAR} ODFTrades Inc. · Educational trading platform & software company · Organised in Canada · All rights reserved.
          <br />
          For privacy, legal, or general inquiries: info@odftrades.com
        </div>
      </div>
    </div>
  );
}
